import { de as deLocale, enGB } from 'date-fns/locale';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { defaultNS, type Language, languages, resources } from './resources';

const STORAGE_KEY = 'fair-doctor-lang';

function readStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (languages as string[]).includes(stored)) return stored as Language;
  } catch {
    // Storage unavailable (private mode) — fall back to the default.
  }
  return 'de';
}

const initialLanguage = readStoredLanguage();
document.documentElement.lang = initialLanguage;

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: 'de',
  defaultNS,
  ns: Object.keys(resources.de),
  interpolation: { escapeValue: false },
});

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // Ignore — the choice just won't persist.
  }
});

export { i18n };

/** Current UI language plus a setter; re-renders on change. */
export function useLanguage() {
  const { i18n: instance } = useTranslation();
  const language = (instance.resolvedLanguage ?? 'de') as Language;
  return {
    language,
    setLanguage: (lng: Language) => void instance.changeLanguage(lng),
    /** BCP-47 tag for Intl formatting. */
    locale: language === 'de' ? 'de-DE' : 'en-GB',
    dateLocale: language === 'de' ? deLocale : enGB,
  };
}
